/**
 * Minimal RFC 4180 CSV parser (quotes, escaped quotes, embedded newlines,
 * CRLF/LF, BOM). No external dependency.
 */

export interface ParsedCsv {
  headers: string[];
  records: string[][];
}

export function parseCsv(text: string): ParsedCsv {
  // Strip a UTF-8 byte-order mark (U+FEFF) that Excel prepends to exported CSVs.
  const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  const endField = () => { row.push(field); field = ''; };
  const endRow = () => { endField(); rows.push(row); row = []; };

  while (i < input.length) {
    const ch = input[i];
    if (inQuotes) {
      if (ch === '"') {
        if (input[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += ch; i++; continue;
    }
    if (ch === '"') { inQuotes = true; i++; continue; }
    if (ch === ',') { endField(); i++; continue; }
    if (ch === '\r') { if (input[i + 1] === '\n') i++; endRow(); i++; continue; }
    if (ch === '\n') { endRow(); i++; continue; }
    field += ch; i++;
  }
  // Flush the last field/row unless the input ended cleanly on a newline.
  if (field.length > 0 || row.length > 0) endRow();

  // Drop fully blank trailing rows.
  while (rows.length && rows[rows.length - 1].every(c => c.trim() === '')) rows.pop();

  if (rows.length === 0) return { headers: [], records: [] };
  const [headers, ...records] = rows;
  return { headers: headers.map(h => h.trim()), records };
}

/** Trigger a browser download of text content. */
export function downloadTextFile(filename: string, content: string, mime = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
